/**
 * useInlineToolbarPosition — owns the cursor → statement → pixel-position
 * pipeline for the InlineVqeToolbar. Tracks Monaco's cursor position via the
 * editor handle, classifies the statement at that cursor, debounces position
 * recomputation (150 ms), and re-anchors on scroll. Pure business logic + a
 * thin React hook layer; the heavy lifting is split into testable helpers.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import type { ParseOptions, StatementInfo } from '../../../sql-grammar/model';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import { classifyTabQuery } from '../workspace/classifyTabQuery';
import type { ClassificationResult } from '../workspace/classifyTabQuery';
import type { RenderMode } from '../types';

const DEBOUNCE_MS = 150;

export interface InlineToolbarAnchor {
  readonly visible: true;
  readonly top: number;
  readonly left: number;
  readonly statement: StatementInfo;
  readonly mode: RenderMode;
  readonly cursorOffset: number;
  readonly bufferText: string;
}

export type InlineToolbarState =
  | { readonly visible: false }
  | InlineToolbarAnchor;

const HIDDEN: InlineToolbarState = { visible: false };

export interface UseInlineToolbarPositionArgs {
  readonly editorRef: React.RefObject<SqlMonacoHandle | null>;
  readonly query: string;
  readonly options?: ParseOptions;
  /** When false the hook short-circuits and stays hidden — used to disable in non-SQL tabs. */
  readonly enabled?: boolean;
}

const DEFAULT_OPTIONS: ParseOptions = { engine: 'postgres' };

/**
 * Convert Monaco's 1-based (line, column) to the parsed buffer's 0-based
 * byte offset. `\n` is the only line separator the buffer tracks.
 */
export function lineColumnToOffset(buffer: string, lineNumber: number, column: number): number {
  if (lineNumber < 1) return 0;
  let line = 1;
  let i = 0;
  while (i < buffer.length && line < lineNumber) {
    if (buffer.charCodeAt(i) === 10) line += 1;
    i += 1;
  }
  return Math.min(buffer.length, i + Math.max(0, column - 1));
}

export interface ClassifyAtCursorArgs {
  readonly query: string;
  readonly cursorOffset: number;
  readonly options: ParseOptions;
}

/**
 * Classify the statement at `cursorOffset`. Returns null when the offset is
 * outside any statement (e.g., the cursor is in whitespace between two
 * statements). The render mode is recomputed against the resolved statement
 * — `classifyTabQuery` defaults to statements[0]; we override with the actual
 * statement under the cursor.
 */
export function classifyAtCursor({ query, cursorOffset, options }: ClassifyAtCursorArgs): ClassificationResult | null {
  if (query.trim().length === 0) return null;
  const result = classifyTabQuery(query, options, cursorOffset);
  if (!result.statement) return null;
  return result;
}

export function useInlineToolbarPosition({
  editorRef,
  query,
  options = DEFAULT_OPTIONS,
  enabled = true,
}: UseInlineToolbarPositionArgs): InlineToolbarState {
  const [state, setState] = useState<InlineToolbarState>(HIDDEN);
  const queryRef = useRef(query);
  const optionsRef = useRef(options);
  const enabledRef = useRef(enabled);

  useEffect(() => { queryRef.current = query; }, [query]);
  useEffect(() => { optionsRef.current = options; }, [options]);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const recompute = useCallback(() => {
    if (!enabledRef.current) {
      setState(HIDDEN);
      return;
    }
    const editor = editorRef.current;
    if (!editor) {
      setState(HIDDEN);
      return;
    }
    const cursor = editor.getCursorPosition();
    if (!cursor) {
      setState(HIDDEN);
      return;
    }
    const currentQuery = queryRef.current;
    const cursorOffset = lineColumnToOffset(currentQuery, cursor.lineNumber, cursor.column);
    const classified = classifyAtCursor({
      query: currentQuery,
      cursorOffset,
      options: optionsRef.current,
    });
    if (!classified || !classified.statement) {
      setState(HIDDEN);
      return;
    }
    const statement = classified.statement;
    const anchorLine = statement.range.startLine + 1;
    const pixel = editor.getPixelPositionForPosition(anchorLine, 1);
    if (!pixel) {
      setState(HIDDEN);
      return;
    }
    setState({
      visible: true,
      top: Math.max(0, pixel.top),
      left: Math.max(0, pixel.left),
      statement,
      mode: classified.renderMode,
      cursorOffset,
      bufferText: currentQuery,
    });
  }, [editorRef]);

  const debouncedRecompute = useDebouncedCallback(recompute, DEBOUNCE_MS);

  useEffect(() => {
    if (!enabled) {
      setState(HIDDEN);
      return undefined;
    }
    let cancelled = false;
    let unsubscribeCursor: (() => void) | null = null;
    let unsubscribeScroll: (() => void) | null = null;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    // Monaco mounts asynchronously; the SqlMonacoHandle's underlying editor
    // may not be ready when this effect first runs. Poll until
    // getCursorPosition() returns a real position, then subscribe.
    const tryAttach = () => {
      if (cancelled) return;
      const editor = editorRef.current;
      if (!editor) {
        pollTimer = setTimeout(tryAttach, 100);
        return;
      }
      const cursor = editor.getCursorPosition();
      if (!cursor) {
        recompute();
        pollTimer = setTimeout(tryAttach, 100);
        return;
      }
      recompute();
      unsubscribeCursor = editor.onDidChangeCursorPosition(() => debouncedRecompute());
      unsubscribeScroll = editor.onDidScrollChange(() => recompute());
    };
    tryAttach();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      unsubscribeCursor?.();
      unsubscribeScroll?.();
    };
  }, [editorRef, recompute, debouncedRecompute, enabled, query]);

  return state;
}
